import { ArrowRight, Search, SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { narratorEdges, narrators } from "../researchData";
import type { NarratorRecord } from "../types";

type Props = {
  selected: NarratorRecord;
  onSelect: (person: NarratorRecord) => void;
};

export function NarratorDirectory({ selected, onSelect }: Props) {
  const [query, setQuery] = useState("");
  const [tier, setTier] = useState("all");
  const visible = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return narrators
      .filter((person) => tier === "all" || String(person.importance_tier) === tier)
      .filter((person) => !normalized || `${person.display_name} ${person.role} ${person.mini_bio}`.toLocaleLowerCase().includes(normalized))
      .sort((a, b) => a.importance_tier - b.importance_tier || a.display_name.localeCompare(b.display_name));
  }, [query, tier]);

  const connectionCount = (id: string) => narratorEdges.filter((edge) => edge.from === id || edge.to === id).length;

  return (
    <section className="narrators-section" id="narrators">
      <div className="section-heading">
        <div>
          <h2>People behind the record</h2>
          <p>Seventy-four narrators, informants, intermediaries, authors, and compilers—ranked by research priority, never blanket trust.</p>
        </div>
        <span className="section-count">74 PROFILES</span>
      </div>
      <div className="directory-tools">
        <label className="search-field"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search people, roles, or source families" /></label>
        <label className="tier-filter"><SlidersHorizontal size={16} /><select value={tier} onChange={(event) => setTier(event.target.value)}><option value="all">All priority tiers</option><option value="1">Tier 1 — central</option><option value="2">Tier 2 — claim critical</option><option value="3">Tier 3 — bridges</option><option value="4">Tier 4 — unresolved</option></select></label>
      </div>
      <div className="directory-table" role="table" aria-label="Narrator registry">
        <div className="directory-head" role="row"><span>Person</span><span>Role</span><span>Dates</span><span>Network</span><span /></div>
        {visible.map((person) => (
          <button className={selected.id === person.id ? "directory-row is-selected" : "directory-row"} role="row" key={person.id} onClick={() => onSelect(person)}>
            <span className="person-cell"><i>P{person.importance_tier}</i><b>{person.display_name}</b><small>{person.mini_bio}</small></span>
            <span className="role-cell">{person.role}</span>
            <span className="date-cell">{person.death ?? "Unknown"}</span>
            <span className="network-cell">{connectionCount(person.id)} mapped edges</span>
            <ArrowRight size={17} />
          </button>
        ))}
      </div>
      {!visible.length ? <div className="empty-state">No profiles match this filter. The registry preserves unresolved names rather than silently merging them.</div> : null}
    </section>
  );
}
